/**
 * RedirectHandler - Handles command redirects and completion logic
 */
export class RedirectHandler {
  constructor(commandProcessor) {
    this.commandProcessor = commandProcessor;
  }

  /**
   * Handle command completion with optional redirects
   */
  async handleCompletion(
    step,
    sessionData,
    input = null,
    stepOutput = "",
    callbacks = {}
  ) {
    // Get completion output from command
    const command = sessionData._command || {};
    const completionOutput =
      await this.commandProcessor.processCommandCompletion(
        command,
        sessionData,
        callbacks
      );

    // Check for redirects (step-level first, then command-level)
    const redirectResult = await this.commandProcessor.handleRedirect(
      step,
      command,
      input,
      sessionData,
      callbacks
    );

    if (redirectResult) {
      return this._buildRedirectResponse(
        redirectResult,
        stepOutput,
        completionOutput,
        input,
        sessionData
      );
    }

    // No redirect - return completion
    return this._buildCompletionResponse(stepOutput, completionOutput);
  }

  /**
   * Build redirect response with proper data structure
   */
  _buildRedirectResponse(
    redirectResult,
    stepOutput,
    completionOutput,
    input,
    sessionData
  ) {
    const { command: redirectTarget, payload } = redirectResult;

    // Prepare command data
    const commandData = payload
      ? { previousInput: input, payload }
      : { previousInput: input, ...sessionData };

    // Combine outputs for redirect. Trim trailing newlines from stepOutput
    // first so that a single "\n" separator never compounds with one already
    // baked into the step's outro string.
    let combinedOutput = "";
    if (stepOutput) combinedOutput += stepOutput.replace(/\n+$/, "");
    if (completionOutput) {
      if (combinedOutput) combinedOutput += "\n";
      combinedOutput += completionOutput;
    }

    if (combinedOutput) {
      return {
        output: combinedOutput,
        nextCommand: redirectTarget,
        nextCommandData: commandData,
      };
    } else {
      return {
        startCommand: { target: redirectTarget, data: commandData },
      };
    }
  }

  /**
   * Build completion response without redirect
   */
  _buildCompletionResponse(stepOutput, completionOutput) {
    let output = "";
    if (stepOutput) output += stepOutput.replace(/\n+$/, "");
    if (completionOutput) {
      if (output) output += "\n";
      output += completionOutput;
    }
    console.log("🎯 RedirectHandler - completion response:", {
      stepOutput,
      completionOutput,
      finalOutput: output,
    });
    return { output: output || null };
  }

  /**
   * Handle redirect from non-final step
   */
  async handleMidStepRedirect(
    step,
    input,
    sessionData,
    stepOutput = "",
    callbacks
  ) {
    // Mid-step redirects only support step.redirect (not command.redirect)
    const redirectResult = await this.commandProcessor.handleRedirect(
      step,
      null, // No command context for mid-step redirects
      input,
      sessionData,
      callbacks
    );

    if (!redirectResult) return null;

    const { command: redirectTarget, payload } = redirectResult;
    const commandData = payload
      ? { previousInput: input, payload }
      : { previousInput: input, ...sessionData };

    if (stepOutput) {
      return {
        output: stepOutput,
        nextCommand: redirectTarget,
        nextCommandData: commandData,
      };
    } else {
      return {
        startCommand: { target: redirectTarget, data: commandData },
      };
    }
  }
}
